import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user: null (loading) | object (authed) | false (anon)
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);

  const loadWorkspace = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/workspace");
      setWorkspace(data);
    } catch {
      setWorkspace(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadWorkspace();
    } catch {
      setUser(false);
      setWorkspace(null);
    }
  }, [loadWorkspace]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      await loadWorkspace();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e, "Login failed.") };
    }
  };

  const signup = async (payload) => {
    try {
      const { data } = await api.post("/auth/signup", payload);
      setUser(data);
      await loadWorkspace();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e, "Signup failed.") };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
    setWorkspace(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, workspace, login, signup, logout, refresh, loadWorkspace }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
